import React, { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";

import EventService from "service/event/EventService";
import DailyScheduleService from "./DailyScheduleService";
import HoverTip from "../common/HoverTip";
import {
  SLEEP_QUALITY_OPTIONS,
  formatDuration,
  formatTimeAmPm,
  glassesToLiters,
  ML_PER_GLASS,
  deriveSleep,
} from "./data";
import { DS_V2_REFRESH } from "./events";

/**
 * Hydration & sleep card. Two blocks share one Save button (per the
 * user spec): controls are always local until Save is clicked.
 *
 *   1. Hydration — Water intake (glasses input; auto-converts to L via
 *      the 300 ml/glass constant); Target (L, decimal input).
 *
 *   2. Sleep — Sleep time / Wake time / Duration are ALL derived from the
 *      "Sleep" and "Wake up" timeline entries and shown read-only. Sleep
 *      quality is the only user-editable field — Good / Fair / Poor
 *      segmented pill.
 *
 * Save fires the two PUTs sequentially (hydration then sleep). Cancel
 * reverts to the server snapshot. Both buttons stay visible always —
 * Save is disabled when nothing differs from the server. This replaced a
 * 1s debounce on hydration plus a save-on-click on quality.
 *
 * Px-matched to "Card · Hydration & sleep" (3485:6644, 832×298), including
 * the header's Cancel / Save pair (4151:9256 — 66×24 then 72×24, 10px apart).
 */
// Server-side caps (api-daily-schedule-v2-service validations).
const MAX_GLASSES = 50;
const MAX_TARGET_L = 20;

const HydrationSleepCard = ({ patientId, date, doc, onConflict, onSaved }) => {
  const version = (doc && doc.version) || 0;
  const serverHydration = (doc && doc.hydration) || {};
  const serverSleep = (doc && doc.sleep) || {};
  const entries = (doc && doc.entries) || [];

  const [glasses, setGlasses] = useState(serverHydration.glasses || 0);
  const [targetL, setTargetL] = useState(serverHydration.targetL || 0);
  const [quality, setQuality] = useState(serverSleep.quality || "");
  const [saveState, setSaveState] = useState("idle"); // idle | saving | saved | error
  const [saveError, setSaveError] = useState(null);

  // Reset local state whenever the day changes or its doc reloads. Keyed
  // on (date, version) so an in-flight edit is not stomped by a stale
  // render of the same day, but switching days always shows that day's
  // values.
  useEffect(() => {
    setGlasses(serverHydration.glasses || 0);
    setTargetL(serverHydration.targetL || 0);
    setQuality(serverSleep.quality || "");
    setSaveState("idle");
    setSaveError(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [date, version]);

  const derived = deriveSleep(entries);

  // Both hydration inputs clamp on the wire to the server's own ranges
  // (validations.ValidateHydration: glasses ≤ 50, targetL ≤ 20) — see the
  // two number inputs below. The derived floors stay at 0 so an untouched
  // form still passes the dirty check against the server's default 0.
  const glassesNum = Math.max(0, Math.min(MAX_GLASSES, Number(glasses) || 0));
  const targetNum = Math.max(0, Math.min(MAX_TARGET_L, Number(targetL) || 0));

  const hydrationDirty =
    glassesNum !== (serverHydration.glasses || 0) ||
    targetNum !== (serverHydration.targetL || 0);
  const sleepDirty = quality !== (serverSleep.quality || "");
  const isDirty = hydrationDirty || sleepDirty;

  const cancelEdits = () => {
    setGlasses(serverHydration.glasses || 0);
    setTargetL(serverHydration.targetL || 0);
    setQuality(serverSleep.quality || "");
    setSaveError(null);
    setSaveState("idle");
  };

  // Save runs the two PUTs in sequence — hydration first, then sleep. If
  // hydration succeeds and sleep fails, the hydration change is already
  // persisted; we surface the sleep error and leave the local sleep pill
  // dirty so the user can retry. A refresh is emitted after each
  // successful call so the derived sleep-time / duration columns pick up
  // any concurrent timeline edits.
  const save = () => {
    if (!isDirty || saveState === "saving") return;
    setSaveState("saving");
    setSaveError(null);

    const svc = new DailyScheduleService();

    // A 409 carries the live day — hand it to the parent (which swaps the
    // doc, and our (date, version) effect then re-syncs the inputs).
    const failed = (label) => (err) => {
      if (onConflict && onConflict(err)) {
        setSaveState("idle");
        return;
      }
      setSaveState("error");
      setSaveError((err && err.message) || `${label} save failed`);
    };
    const finished = () => {
      setSaveState("saved");
      if (onSaved) onSaved();
      EventService.emit(DS_V2_REFRESH);
    };

    const saveSleepIfDirty = (v) => {
      if (!sleepDirty) {
        finished();
        return;
      }
      const putSleep = (expected) =>
        svc.updateSleep(
          patientId,
          date,
          { quality, expectedVersion: expected || 0 },
          finished,
          failed("Sleep-quality")
        );
      // The hydration PUT bumped the version. Prefer the version it echoed
      // back; if it echoed none, RE-READ rather than falling back to 0 —
      // guessing here posts a stale expectedVersion and the write 409s.
      if (typeof v === "number") {
        putSleep(v);
        return;
      }
      svc.list(patientId, date, (fresh) => putSleep(fresh && fresh.version), failed("Sleep-quality"));
    };

    if (hydrationDirty) {
      svc.updateHydration(
        patientId,
        date,
        {
          glasses: glassesNum,
          targetL: targetNum,
          expectedVersion: version || 0,
        },
        (res) => saveSleepIfDirty(res && res.version),
        failed("Hydration")
      );
      return;
    }
    saveSleepIfDirty(version);
  };

  const litreDisplay = useMemo(() => glassesToLiters(glassesNum).toFixed(1), [glassesNum]);

  return (
    <div className="lcard ds3-hyd">
      <header className="lcard-head">
        <h2 className="lcard-title">Hydration &amp; sleep</h2>
        <span className="lcard-meta">
          {saveState === "saving" && "Saving…"}
          {saveState === "saved" && !isDirty && "Saved"}
          {saveState === "error" && saveError && (
            <span className="fam-warn is-error">{saveError}</span>
          )}
        </span>
        {/* Board 4151:9256 — Cancel 66×24 then Save 72×24, 10px apart. */}
        <div className="ds3-actions">
          <button
            type="button"
            className="ds3-cancel"
            onClick={cancelEdits}
            disabled={!isDirty || saveState === "saving"}
          >
            Cancel
          </button>
          <button
            type="button"
            className="ds3-save"
            onClick={save}
            disabled={!isDirty || saveState === "saving"}
          >
            {saveState === "saving" ? "Saving…" : "Save"}
          </button>
        </div>
      </header>

      {/* Hydration block */}
      <section className="ds-section">
        <h3 className="ds-subhead">Hydration</h3>
        <div className="ds-grid">
          <div className="ds-field">
            {/* `.ds-field-label` uses `text-overflow: ellipsis`, so the long
                Water intake label clips on narrower widths. HoverTip
                surfaces the full string in a white portalled pill on
                hover — the OS/native title we'd otherwise fall back on
                is yellow-styled and out of brand. */}
            <HoverTip text={`Water intake (1 glass = ${ML_PER_GLASS} ml)`}>
              <label className="ds-field-label" htmlFor="ds-glasses">
                Water intake (1 glass = {ML_PER_GLASS} ml)
              </label>
            </HoverTip>
            <div className="ds-input">
              {/* Integer 1–50. Native min/max cap the spinner buttons
                  and browser validation, but Chrome / Safari still accept
                  keyboard-typed values past them — the onChange strips
                  non-digits and clamps so state is always a stringified
                  integer in-range (or empty for a mid-edit clear). */}
              {/* No native `min` / `max` here on purpose. The browser's
                  HTML5 validation tooltip ("Value must be greater than or
                  equal to 1") is OS-styled (grey on macOS / yellow on
                  Windows) and off-brand — the reviewer sees a garish popup
                  whenever the server-sent initial 0 sits in the input. The
                  onChange handler below still clamps every user-typed
                  value into [1, 50], so removing the HTML attrs affects
                  only the validation UI, not the persisted range. */}
              <input
                id="ds-glasses"
                type="number"
                step="1"
                value={glasses}
                onChange={(e) => {
                  const raw = e.target.value;
                  if (raw === "") { setGlasses(""); return; }
                  const n = parseInt(raw.replace(/[^0-9]/g, ""), 10);
                  if (isNaN(n)) { setGlasses(""); return; }
                  setGlasses(String(Math.min(MAX_GLASSES, Math.max(1, n))));
                }}
              />
              <span className="ds-input-suffix">glasses</span>
            </div>
          </div>

          <div className="ds-field">
            <label className="ds-field-label">Auto-converts to</label>
            <div className="ds-input is-readonly">
              <input value={litreDisplay} readOnly aria-label="Auto-converts to" />
              <span className="ds-input-suffix">L</span>
            </div>
          </div>

          <div className="ds-field">
            {/* Matching the Water-intake tooltip pattern above — same
                white portalled pill so long labels have consistent
                reveal behaviour, and future edits to the label text
                don't accidentally lose the tooltip. */}
            <HoverTip text="Target daily water intake (litres)">
              <label className="ds-field-label" htmlFor="ds-target">
                Target
              </label>
            </HoverTip>
            <div className="ds-input">
              {/* Integer 1–20 L. Clamp mirrors the
                  Water intake input — strip non-digits, cap on change. */}
              {/* Same reason as Water intake — no native `min`/`max` so
                  the OS-styled validation tooltip stays off. onChange
                  keeps the persisted value inside [1, 20]. */}
              <input
                id="ds-target"
                type="number"
                step="1"
                value={targetL}
                onChange={(e) => {
                  const raw = e.target.value;
                  if (raw === "") { setTargetL(""); return; }
                  const n = parseInt(raw.replace(/[^0-9]/g, ""), 10);
                  if (isNaN(n)) { setTargetL(""); return; }
                  setTargetL(String(Math.min(MAX_TARGET_L, Math.max(1, n))));
                }}
              />
              <span className="ds-input-suffix">L</span>
            </div>
          </div>
        </div>
      </section>

      {/* Sleep block */}
      <section className="ds-section">
        <h3 className="ds-subhead">
          Sleep
          <span className="ds-subhead-meta">
            {" "}· sleep time, wake time and duration are derived from your
            timeline
          </span>
        </h3>
        <div className="ds-grid is-sleep">
          <div className="ds-field">
            <label className="ds-field-label">Sleep time</label>
            <div className="ds-input is-readonly">
              <input
                value={derived.sleepMins != null ? formatTimeAmPm(derived.sleepMins) : "—"}
                readOnly
                aria-label="Sleep time"
              />
            </div>
          </div>

          <div className="ds-field">
            <label className="ds-field-label">Wake time</label>
            <div className="ds-input is-readonly">
              <input
                value={derived.wakeMins != null ? formatTimeAmPm(derived.wakeMins) : "—"}
                readOnly
                aria-label="Wake time"
              />
            </div>
          </div>

          <div className="ds-field">
            <label className="ds-field-label">Duration</label>
            <div className="ds-input is-readonly">
              <input
                value={derived.durationMins != null ? formatDuration(derived.durationMins) : "—"}
                readOnly
                aria-label="Duration"
              />
            </div>
          </div>

          <div className="ds-field ds-field-quality">
            <label className="ds-field-label">Sleep quality</label>
            <div className="ds-seg" role="radiogroup" aria-label="Sleep quality">
              {SLEEP_QUALITY_OPTIONS.map((o) => (
                <button
                  key={o.key}
                  type="button"
                  role="radio"
                  aria-checked={quality === o.key}
                  className={`ds-seg-cell${quality === o.key ? " is-on" : ""}`}
                  onClick={() => setQuality(o.key)}
                >
                  {o.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        {(derived.sleepMins == null || derived.wakeMins == null) && (
          <p className="ds-sleep-hint">
            Add rows named <strong>Sleep</strong> and <strong>Wake up</strong>{" "}
            to the timeline to auto-fill the sleep block.
          </p>
        )}
      </section>
    </div>
  );
};

HydrationSleepCard.propTypes = {
  patientId: PropTypes.number.isRequired,
  date: PropTypes.string.isRequired, // YYYY-MM-DD — the day being edited
  doc: PropTypes.object,
  // Returns true when it handled a 409 (rebased the parent on `current`).
  onConflict: PropTypes.func,
  // Fired after a successful save so the filled-days index can refresh.
  onSaved: PropTypes.func,
};

export default HydrationSleepCard;
